import { consola } from "consola";
import { useSettingsStore } from "@/stores/settings";

type TrackingEventName =
  | "sign_up"
  | "deposit"
  | "deposit_approved"
  | "bet_transaction"
  | "kyc_pending"
  | "kyc_approved";

interface DepositEventData {
  amount: number;
  currency: string;
  id: string;
  status: "pending" | "approved";
}

interface DepositData extends DepositEventData {
  // Number of deposits occurrences
  is_ftd?: boolean;
  is_std?: boolean;
  is_ttd?: boolean;
  is_qtd?: boolean;
  occurrence?: number;

  // Bonus
  bonus_amount?: number;
  bonus_id?: string;
  is_bonus_eligible?: boolean;
}

interface SignUpEventData {
  source?: string;
  referrer?: string;
}

interface KYCEventData {
  document_type: string;
  status: string;
}

interface BetTransactionEventData {
  value: number;
  virtual_currency_name: string;
  game_category: 'Cassino' | 'Esportes';
  item_name: string;
}

interface EventData<T extends TrackingEventName> {
  deposit?: DepositEventData;
  deposit_approved?: DepositEventData & { occurrence: number };
  sign_up?: SignUpEventData;
  kyc_pending?: KYCEventData;
  kyc_approved?: KYCEventData;
  bet_transaction?: BetTransactionEventData;
}

export const useTrackingStore = defineStore("tracking", () => {
  const settingsStore = useSettingsStore();
  const authStore = useAuthStore();

  const handleDepositData = async (depositData: DepositEventData & { occurrence?: number }): Promise<DepositData> => {
    const currentDepositData: DepositData = {
      ...depositData,
      is_ftd: depositData?.occurrence === 1,
      is_std: depositData?.occurrence === 2,
      is_ttd: depositData?.occurrence === 3,
      is_qtd: depositData?.occurrence === 4,
      occurrence: depositData?.occurrence
    };

    return currentDepositData;
  };

  const actions = {

    // Here we initialize the GTM service
    initialize() {
      if (import.meta.server) return;

      if (settingsStore.settings?.pixel_gtm === "null") {
        /* @__PURE__ */ consola.warn("Google Tag Manager ID is not set");
        return;
      };

      if (!import.meta.server && settingsStore.settings?.pixel_gtm) {

        /**
         * @todo - Change this to the backoffice on the future
         * 
         * This is a temporary solution to load the custom GTM script for 
         * lotogreen.bet.br, br4.bet.br and goldebet.bet.br.
         */
        if (useRuntimeConfig().public.APP_ID == "loto") {
          /* @__PURE__ */ consola.success("Loading custom GTM script for lotogreen.bet.br");

          // @ts-ignore
          (function (w, d, s, l, i) { w[l] = w[l] || []; w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' }); var f = d.getElementsByTagName(s)[0], j = d.createElement(s); j.async = true; j.src = "https://gtm.lotogreen.bet.br/bahtpficue.js?" + i; f.parentNode.insertBefore(j, f); })(window, document, 'script', 'dataLayer', 'bur6m=aWQ9R1RNLVBSS1dDVE1Q&page=1')
        }
        
        if (useRuntimeConfig().public.APP_ID == "br4bet") {
          /* @__PURE__ */ consola.success("Loading custom GTM script for br4.bet.br");

          // @ts-ignore
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src="https://gtm.br4.bet.br/702bgvgvirv.js?"+i;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','c211y=aWQ9R1RNLU03S0paV00%3D&sort=desc');
        }

        if (useRuntimeConfig().public.APP_ID == "goldebet") {
          /* @__PURE__ */ consola.success("Loading custom GTM script for goldebet.bet.br");

          // @ts-ignore
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src="https://gtm.goldebet.bet.br/8segsrnwxlvpr.js?"+i;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','apc3=aWQ9R1RNLU5UVFZXUTI2&page=2');
        }
        
        else {
          (function (w: { [key: string]: any }, d: Document, s: string, l: string, i: string) {
            w[l] = w[l] || [];
            w[l].push({
              "gtm.start": new Date().getTime(),
              event: "gtm.js"
            });
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s) as HTMLScriptElement,
              dl = l != "dataLayer" ? "&l=" + l : "";
            j.async = true;
            j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
            if (f && f.parentNode) {
              f.parentNode.insertBefore(j, f);
            } else {
              d.getElementsByTagName("head")[0].appendChild(j);
            }
          })(window, document, "script", "dataLayer", settingsStore.settings.pixel_gtm);
        }

        /* @__PURE__ */ consola.success('Google Tag Manager initialized');

      }
    },

    async sendEvent<T extends TrackingEventName>(
      eventName: T,
      data: EventData<T>[T]
    ) {
      if (import.meta.server || typeof window === "undefined") {
        /* @__PURE__ */ consola.error("You can't send a tracking event on the server side.");
        return;
      }

      if (!window.dataLayer) {
        /* @__PURE__ */ consola.error("GTM dataLayer was not found");
        return;
      }

      if (eventName === "deposit") {
        const depositData = data as DepositEventData;
        const updatedData = await handleDepositData(depositData);
        data = updatedData as typeof data;
      }

      if (eventName === "deposit_approved") {
        console.log("deposit_approved", data);
        const depositData = data as DepositEventData & { occurrence: number };
        const updatedDepositData = await handleDepositData(depositData);
        data = updatedDepositData as typeof data;
      }

      if (eventName === "bet_transaction") {
        const transactionData = data as BetTransactionEventData;
        const updatedData = {
          value: transactionData.value,
          virtual_currency_name: transactionData.virtual_currency_name,
          game_category: transactionData.game_category,
          item_name: transactionData.item_name,
        }
        data = updatedData as typeof data;
      }

      // @TODO - Verify if user has 2fa enabled
      // @TODO - Ask backend team about this
      // "has_2fa_enabled": boolean;

      // @TODO - Verify the status of kyc
      // "kyc_status": boolean;

      window.dataLayer.push({
        event: eventName === "deposit_approved" ? "deposit" : eventName === "bet_transaction" ? "spend_virtual_currency" : eventName,
        data: { ...data },
        user: {
          id: authStore.state.user?.id!,
          name: authStore.state.user?.name!,
          email: authStore.state.user?.email!,
          phone: authStore.state.user?.phone!,
          kyc_status: authStore.state.user?.status === 'Ativo',
          has_2fa_enabled: false
        },
        created_at: Date.now()
      });

      /* @__PURE__ */ consola.success(
        `%c${eventName}%c event was sent`,
        `color: #FFD700; font-weight: bold;`,
        "color: inherit;",
        window.dataLayer[window.dataLayer.length - 1]
      );
    }
  };

  return {
    actions
  };
});